/**
 * MobileReportFormModal
 *
 * Full-screen MUI Dialog replacement for ReportFormModal (React Bootstrap Modal).
 * Used when isMobile === 2 for creating reports and exports.
 * Fields: Month (TransparentDatePicker), Company (SelectInput), Client (SelectInput), Description (TextField).
 *
 * Components from MUI: Dialog, AppBar, Toolbar, Typography, IconButton, Button, TextField, Box, Slide.
 * Shared project components: TransparentDatePicker, SelectInput (kept as-is).
 */
import React, { useState, useEffect, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  TextField,
  Box,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Langs from "../../../app/lang/langs";
import { TransparentDatePicker } from "../../../components/buttons/TransparentDatePicker";
import { SelectInput } from "../../../components/inputs/Select";
import { generateReport } from "../reportsSlice";
import { generateUniqueId } from "../../../app/utils/helpers";
import moment from "moment";

/* ------------------------------------------------------------------ */
/*  Transition                                                         */
/* ------------------------------------------------------------------ */
const SlideUp = forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

/**
 * @param {Object} props
 * @param {string|null} props.type - "NEW_REPORT" | "NEW_EXPORT" | null
 * @param {boolean} props.isSilae - whether this is a SILAE export
 * @param {Function} props.onHide - close modal
 * @param {Function} props.onSubmit - called after successful creation with date
 */
export default function MobileReportFormModal({
  type,
  isSilae,
  onHide,
  onSubmit,
}) {
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settings);
  const teams = useSelector((state) => state.teams.allTeams);
  const selectedDate = useSelector((state) => state.reports.selectedDate);

  const [formError, setFormError] = useState(null);
  const [formData, setFormData] = useState({
    date: selectedDate ? new Date(selectedDate) : new Date(),
  });

  useEffect(() => {
    if (selectedDate) {
      setFormData((prev) => ({ ...prev, date: selectedDate }));
    }
  }, [selectedDate]);

  // Reset form when modal opens
  useEffect(() => {
    if (type) {
      setFormData({
        date: selectedDate ? new Date(selectedDate) : new Date(),
      });
      setFormError(null);
    }
  }, [type]);

  const handleCompanyChange = (option) => {
    setFormData({ ...formData, id_company: option.value });
  };

  const handleClientChange = (option) => {
    setFormData({ ...formData, id_team: option.value });
  };

  const handleDescriptionChange = (e) => {
    setFormData({ ...formData, description: e.target.value });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, date: date });
  };

  function handleCreate() {
    if (!formData.date) {
      setFormError("date");
      return;
    }
    setFormError(null);
    let id = generateUniqueId();
    let date = moment(formData.date).format("MM-YYYY");
    let filename = `EXPORT_${date}_${id}.xlsx`;
    if (formData.id_company) {
      filename = `${formData.id_company}_${date}_${id}.xlsx`;
    }
    let reportData = {
      id: id,
      date: date,
      description: formData.description,
      status: "CREATED",
      is_silae: isSilae,
      filename: filename,
    };
    if (formData.id_team) reportData.id_team = formData.id_team;
    if (formData.id_company) reportData.id_company = formData.id_company;
    dispatch(generateReport(reportData)).then(() => {
      onSubmit(formData.date);
    });
    onHide();
  }

  const open = Boolean(type);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onHide}
      TransitionComponent={SlideUp}
      sx={{ zIndex: 1400 }}
    >
      {/* AppBar header */}
      <AppBar
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(20px) saturate(1.4)",
          WebkitBackdropFilter: "blur(20px) saturate(1.4)",
          color: "rgb(0,0,0)",
          boxShadow:
            "0 4px 6px -4px rgba(0,40,128,0.05), 0 2px 3px -2px rgba(0,0,0,0.03)",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <IconButton
            edge="start"
            onClick={onHide}
            sx={{
              color: "rgba(0,0,0,0.7)",
              transition: TRANSITION_SMOOTH,
              "&:hover": {
                backgroundColor: "rgba(255,144,21,0.08)",
                color: "rgb(255,144,21)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.05rem",
              letterSpacing: "-0.02em",
            }}
          >
            <Langs str={type} />
          </Typography>
          <Box sx={{ width: 48 }} />
        </Toolbar>
      </AppBar>

      {/* Form body */}
      <Box
        sx={{
          flex: 1,
          px: 2.5,
          py: 3,
          bgcolor: "var(--surface-ground, #f8f9fa)",
        }}
      >
        {/* Month field */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              mb: 1,
              fontSize: "0.85rem",
              color: "rgba(0,0,0,0.65)",
            }}
          >
            <Langs str="Month" />
          </Typography>
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.85)",
              borderRadius: "14px",
              p: 1.5,
              border: formError === "date"
                ? "1.5px solid #d32f2f"
                : "1px solid rgba(0,0,0,0.06)",
              transition: TRANSITION_SMOOTH,
            }}
          >
            <TransparentDatePicker
              viewType="MONTHLY"
              onChange={handleDateChange}
              value={formData.date}
            />
          </Box>
        </Box>

        {/* Company field */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              mb: 1,
              fontSize: "0.85rem",
              color: "rgba(0,0,0,0.65)",
            }}
          >
            <Langs str="COMPANY" />
          </Typography>
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.85)",
              borderRadius: "14px",
              p: 1,
              border: "1px solid rgba(0,0,0,0.06)",
              transition: TRANSITION_SMOOTH,
              "& input": { fontSize: "16px" },
            }}
          >
            <SelectInput
              value={formData.id_company}
              onChange={handleCompanyChange}
              error={formError === "id_company"}
              options={[
                { label: "All", value: null },
                ...settings.companies,
              ]}
            />
          </Box>
        </Box>

        {/* Client field */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              mb: 1,
              fontSize: "0.85rem",
              color: "rgba(0,0,0,0.65)",
            }}
          >
            <Langs str="CLIENT" />
          </Typography>
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.85)",
              borderRadius: "14px",
              p: 1,
              border: "1px solid rgba(0,0,0,0.06)",
              transition: TRANSITION_SMOOTH,
              "& input": { fontSize: "16px" },
            }}
          >
            <SelectInput
              value={formData.id_team}
              onChange={handleClientChange}
              options={[{ label: "All", value: null }, ...teams]}
            />
          </Box>
        </Box>

        {/* Description field */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              mb: 1,
              fontSize: "0.85rem",
              color: "rgba(0,0,0,0.65)",
            }}
          >
            <Langs str="DESCRIPTION" />
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={formData.description || ""}
            onChange={handleDescriptionChange}
            inputProps={{ maxLength: 200 }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: "rgba(255,255,255,0.85)",
                fontSize: "16px",
                transition: TRANSITION_SMOOTH,
                "& fieldset": {
                  borderColor: "rgba(0,0,0,0.06)",
                  transition: TRANSITION_SMOOTH,
                },
                "&:hover fieldset": {
                  borderColor: "rgba(0,0,0,0.15)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "warning.main",
                  boxShadow: "0 0 0 3px rgba(255,144,21,0.15)",
                },
              },
              "& textarea": { fontSize: "16px" },
            }}
          />
        </Box>
      </Box>

      {/* Bottom action buttons */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          gap: 1.5,
          px: 2.5,
          py: 2,
          bgcolor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(20px) saturate(1.4)",
          WebkitBackdropFilter: "blur(20px) saturate(1.4)",
          boxShadow:
            "0 -4px 6px -4px rgba(0,40,128,0.05), 0 -2px 3px -2px rgba(0,0,0,0.03)",
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={onHide}
          sx={{
            borderRadius: 999,
            minHeight: 48,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
            borderColor: "rgba(0,0,0,0.15)",
            color: "rgba(0,0,0,0.7)",
            transition: TRANSITION_SMOOTH,
            "&:hover": {
              borderColor: "rgba(0,0,0,0.3)",
              bgcolor: "rgba(0,0,0,0.03)",
            },
            "&:active": { transform: "scale(0.97)" },
          }}
        >
          <Langs str="CANCEL" />
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleCreate}
          sx={{
            borderRadius: 999,
            minHeight: 48,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
            bgcolor: "#FF9015",
            color: "#fff",
            boxShadow: "0 4px 14px rgba(255,144,21,0.35)",
            transition: TRANSITION_SMOOTH,
            "&:hover": {
              bgcolor: "#e67e00",
              boxShadow: "0 6px 20px rgba(255,144,21,0.45)",
            },
            "&:active": { transform: "scale(0.97)" },
          }}
        >
          <Langs str="CREATE" />
        </Button>
      </Box>
    </Dialog>
  );
}
